import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from './api';
import LeagueQRScanner from './LeagueQRScanner';
import './styles/index.css';
import './styles/league-access.css';
import SponsorsSection from './SponsorsSection';
import DashboardHeader from './DashboardHeader';

const LEAGUE_RESUME_VIEW_KEY = 'dartleague-league-resume-view';
const SPONSOR_CONTACT_URL = 'https://wa.me/34627367531?text=Hola,%20estoy%20interesado%20en%20ser%20patrocinador%20de%20501%20Darts';

const getInitialLeagueView = () => {
  if (typeof window === 'undefined') {
    return 'overview';
  }

  const storedView = sessionStorage.getItem(LEAGUE_RESUME_VIEW_KEY);
  sessionStorage.removeItem(LEAGUE_RESUME_VIEW_KEY);

  return storedView === 'play' ? 'play' : 'overview';
};

const formatMatchStatValue = (value, options = {}) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return options.fallback || '-';
  }

  const decimals = options.decimals ?? 0;
  const suffix = options.suffix || '';
  return `${numericValue.toFixed(decimals)}${suffix}`;
};

const Dashboard = ({ onBack, onLeagueViewChange }) => {
  const [user] = useState(api.getCurrentUser());
  const [ligas, setLigas] = useState([]);
  const [selectedLiga, setSelectedLiga] = useState(null);
  const [clasificacion, setClasificacion] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [promedioGlobal, setPromedioGlobal] = useState({ promedioTotal: 0, porcentajeVictorias: 0, promedioOnline: null });
  const [loading, setLoading] = useState(true);
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const [error, setError] = useState('');
  const [expandedJornadas, setExpandedJornadas] = useState(new Set());
  const [expandedPartidas, setExpandedPartidas] = useState(new Set());
  const [searchRival, setSearchRival] = useState('');
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [leagueView, setLeagueView] = useState(() => getInitialLeagueView());
  const sponsorsRef = useRef(null);

  useEffect(() => {
    onLeagueViewChange?.(leagueView);
  }, [leagueView, onLeagueViewChange]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    loadLigas();
  }, []);

  // Detectar cuando la tabla tiene scroll horizontal
  useEffect(() => {
    const checkScroll = () => {
      const containers = document.querySelectorAll('.table-container');
      containers.forEach(container => {
        if (container.scrollWidth > container.clientWidth) {
          container.classList.add('has-scroll');
        } else {
          container.classList.remove('has-scroll');
        }
      });
    };

    // Verificar al cargar y al redimensionar
    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    // Verificar después de que la clasificación se haya cargado
    const timeoutId = setTimeout(checkScroll, 100);

    return () => {
      window.removeEventListener('resize', checkScroll);
      clearTimeout(timeoutId);
    };
  }, [clasificacion, loading]);

  const loadPromedioGlobal = useCallback(async () => {
    try {
      setLoadingGlobal(true);
      const [data, onlineStats] = await Promise.all([
        api.getPromedioTotal(),
        api.getOnlineStats()
      ]);
      
      setPromedioGlobal({
        promedioTotal: data.promedioTotal || 0,
        porcentajeVictorias: data.porcentajeVictorias || 0,
        promedioOnline: onlineStats?.promedioOnline ?? null
      });
    } catch (err) {
      console.error('❌ Error cargando promedio global:', err);
      setPromedioGlobal({ promedioTotal: 0, porcentajeVictorias: 0, promedioOnline: null });
    } finally {
      setLoadingGlobal(false);
    }
  }, []);

  const loadLigas = async () => {
    try {
      setLoading(true);
      const ligasData = await api.getMyLeagues();
      setLigas(ligasData);
      
      if (ligasData.length > 0) {
        setSelectedLiga(ligasData[0]._id);
      }
    } catch (err) {
      setError('Error cargando ligas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLigaData = useCallback(async () => {
    try {
      setLoading(true);
      const [clasif, jorn] = await Promise.all([
        api.getClasificacion(selectedLiga),
        api.getJornadas(selectedLiga)
      ]);
      
      setClasificacion(clasif);
      setJornadas(jorn);
    } catch (err) {
      setError('Error cargando datos de la liga');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedLiga]);

  useEffect(() => {
    if (selectedLiga) {
      loadLigaData();
    }
  }, [selectedLiga, loadLigaData]);

  // Cargar promedio global solo una vez al montar (no depende de la liga)
  useEffect(() => {
    loadPromedioGlobal();
  }, [loadPromedioGlobal]);

  const handleLogout = () => {
    api.logout();
    window.location.reload();
  };

  const getLigaActual = () => {
    return ligas.find(l => l._id === selectedLiga);
  };

  const toggleJornada = (jornadaNum) => {
    setExpandedJornadas(prev => {
      // Si ya está abierta, cerrarla
      if (prev.has(jornadaNum)) {
        return new Set();
      }
      // Si no está abierta, cerrar todas y abrir solo esta
      // Hacer scroll a la jornada después de un breve delay para que se expanda primero
      setTimeout(() => {
        const jornadaElement = document.querySelector(`[data-jornada="${jornadaNum}"]`);
        if (jornadaElement) {
          jornadaElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
      
      return new Set([jornadaNum]);
    });
  };

  const togglePartida = (partidaId) => {
    setExpandedPartidas(prev => (prev.has(partidaId) ? new Set() : new Set([partidaId])));
  };

  const jornadasFiltradas = useMemo(() => {
    if (!searchRival.trim() || searchRival.trim().length < 2) {
      return jornadas; // Si no hay búsqueda o tiene menos de 2 letras, mostrar todas
    }

    // Filtrar jornadas que tienen partidas donde YO juego contra el rival buscado
    return jornadas.filter(jornada => {
      return jornada.partidas.some(partida => {
        const soyJugador1 = partida.jugador1._id === user._id;
        const soyJugador2 = partida.jugador2._id === user._id;
        
        // Solo si YO participo en la partida
        if (!soyJugador1 && !soyJugador2) return false;
        
        // Obtener el nombre del rival (el otro jugador)
        const nombreRival = soyJugador1 
          ? (partida.jugador2.username || '').toLowerCase()
          : (partida.jugador1.username || '').toLowerCase();
        
        // Verificar si coincide con la búsqueda
        return nombreRival.includes(searchRival.toLowerCase().trim());
      });
    });
  }, [jornadas, searchRival, user._id]);

  const sugerenciasRivales = useMemo(() => {
    if (!searchRival.trim() || searchRival.trim().length < 2 || suggestionSelected) {
      return [];
    }

    const rivalesSet = new Set();
    const searchLower = searchRival.toLowerCase().trim();

    jornadas.forEach(jornada => {
      jornada.partidas.forEach(partida => {
        const soyJugador1 = partida.jugador1._id === user._id;
        const soyJugador2 = partida.jugador2._id === user._id;
        
        if (soyJugador1 || soyJugador2) {
          const nombreRival = soyJugador1 
            ? partida.jugador2.username 
            : partida.jugador1.username;
          
          if (nombreRival && nombreRival.toLowerCase().includes(searchLower)) {
            rivalesSet.add(nombreRival);
          }
        }
      });
    });

    return Array.from(rivalesSet).slice(0, 3); // Máximo 3 sugerencias
  }, [jornadas, searchRival, user._id, suggestionSelected]);

  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const scrollToSponsors = () => {
    sponsorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading && ligas.length === 0) {
    return (
      <div className="dart-league-dashboard">
        <div className="loading">⏳ Cargando...</div>
      </div>
    );
  }

  if (ligas.length === 0) {
    return (
      <div className="dart-league-dashboard">
        <DashboardHeader
          user={user}
          subtitle="Mis Ligas"
          loading={true}
          promedioTotal={0}
          porcentajeVictorias={0}
          onBack={onBack}
          onLogout={handleLogout}
        />

        {/* Mensaje de No Ligas */}
        <div className="empty-state">
          <h2>🎯 Aún no estás en ninguna liga</h2>
          <div className="empty-state-actions">
            {onBack && (
              <button className="torneos-button" onClick={onBack}>
                ← Volver al inicio
              </button>
            )}
          </div>
          <div className="empty-state-info">
            <p>📱 <strong>Mándanos un WhatsApp</strong> y te avisamos de las ligas más cercanas disponibles</p>
            <a 
              href="https://wa.me/34627367531?text=Hola,%20me%20gustaría%20información%20sobre%20ligas%20de%20dardos%20cercanas" 
              target="_blank" 
              rel="noopener noreferrer"
              className="whatsapp-button"
            >
              💬 Contactar por WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  }

  const ligaActual = getLigaActual();

  return (
    <div className="dart-league-dashboard">
      <DashboardHeader
        user={user}
        subtitle="Mis Ligas"
        loading={loadingGlobal}
        promedioTotal={promedioGlobal.promedioTotal}
        porcentajeVictorias={promedioGlobal.porcentajeVictorias}
        onBack={onBack}
        onLogout={handleLogout}
      />

      {leagueView === 'overview' && (
        <div className="league-overview-highlights">
          <div className="league-access-entry">
            <div className="league-access-copy">
              <span className="league-qr-eyebrow">Acceso QR de liga</span>
              <h3>Entra a tus partidos desde aquí</h3>
              <p>
                Escanea el QR de la tablet del admin y abre el acceso en segundos.
              </p>
            </div>

            <button
              className="league-access-trigger"
              onClick={() => setLeagueView('play')}
            >
              <span className="league-access-trigger-copy">
                <span className="league-view-btn-icon" aria-hidden="true">
                  <span className="league-view-btn-icon-core" />
                </span>
                <span className="league-access-trigger-text">
                  <span className="league-view-btn-label">Entrar con QR</span>
                  <span className="league-view-btn-description">Abrir cámara</span>
                </span>
              </span>

              <span className="league-access-trigger-visual" aria-hidden="true">
                <span className="league-access-visual-grid" />
                <span className="league-access-scanframe feature">
                  <span className="league-access-scanframe-core" />
                  <span className="league-access-corner top-left" />
                  <span className="league-access-corner top-right" />
                  <span className="league-access-corner bottom-left" />
                  <span className="league-access-corner bottom-right" />
                  <span className="league-access-scanline" />
                </span>
              </span>
            </button>
          </div>

          <div className="league-overview-sidecards">
            <button className="sponsors-banner sponsors-banner-card" onClick={scrollToSponsors}>
              <div className="sponsors-banner-main">
                <span className="sponsors-banner-icon" aria-hidden="true">
                  <svg viewBox="0 0 48 48" className="sponsors-banner-icon-mark" focusable="false">
                    <circle cx="24" cy="24" r="12" className="sponsors-banner-ring" />
                    <circle cx="24" cy="24" r="4.5" className="sponsors-banner-core" />
                    <path d="M24 8v5M40 24h-5M24 40v-5M8 24h5" className="sponsors-banner-ticks" />
                    <circle cx="33.5" cy="14.5" r="2.5" className="sponsors-banner-accent sponsors-banner-accent-top" />
                    <circle cx="14.5" cy="33.5" r="2.5" className="sponsors-banner-accent sponsors-banner-accent-bottom" />
                  </svg>
                </span>
                <div className="sponsors-banner-copy">
                  <strong>Patrocinadores</strong>
                  <span>Impulsan la app 501 Dart League.</span>
                </div>
                <span className="sponsors-banner-arrow">→</span>
              </div>
            </button>

            <a
              className="sponsors-banner sponsors-banner-card sponsors-banner-card--contact"
              href={SPONSOR_CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contactar para ser patrocinador por WhatsApp"
            >
              <div className="sponsors-banner-main">
                <span className="sponsors-banner-icon sponsors-banner-icon--contact" aria-hidden="true">
                  <svg viewBox="0 0 48 48" className="sponsors-banner-icon-mark sponsors-banner-icon-mark--contact" focusable="false">
                    <circle cx="24" cy="24" r="12" className="sponsors-banner-ring" />
                    <path d="M24 17v14M17 24h14" className="sponsors-banner-contact-plus" />
                    <circle cx="33.5" cy="14.5" r="2.5" className="sponsors-banner-accent sponsors-banner-accent-top" />
                  </svg>
                </span>
                <div className="sponsors-banner-copy">
                  <strong>Hazte patrocinador</strong>
                  <span>Contáctanos por WhatsApp.</span>
                </div>
                <span className="sponsors-banner-arrow">↗</span>
              </div>
            </a>
          </div>
        </div>
      )}

      {error && (
        <div className="error-banner">⚠️ {error}</div>
      )}

      {leagueView === 'play' && (
        <LeagueQRScanner onBack={() => setLeagueView('overview')} />
      )}

      {leagueView !== 'overview' ? null : (
        <>

      {ligas.length > 1 && (
        <div className="liga-selector">
          <label>Liga:</label>
          <select 
            value={selectedLiga} 
            onChange={(e) => setSelectedLiga(e.target.value)}
          >
            {ligas.map(liga => (
              <option key={liga._id} value={liga._id}>
                {liga.nombre}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Clasificación */}
      <div className="section">
        <h2>📊 Clasificación - {ligaActual?.nombre}</h2>
        {loading ? (
          <div className="loading-small">Cargando...</div>
        ) : (
          <>
            <div className="scroll-hint">
              <span className="scroll-icon">👆</span> Desliza para ver todas las columnas
            </div>
            <div className="table-container">
              <table className="clasificacion-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{selectedLiga?.modalidad === 'parejas' ? 'Equipo' : 'Jugador'}</th>
                    <th>PTS</th>
                    <th>PJ</th>
                    <th>G</th>
                    <th>P</th>
                    <th>JG-JP</th>
                    <th>DIF</th>
                    <th>180s</th>
                  </tr>
                </thead>
                <tbody>
                  {clasificacion.map((item, index) => {
                    const diferencia = (item.juegosGanados || 0) - (item.juegosPerdidos || 0);
                    const total180s = item.ciento80s || item.total180s || 0;
                    
                    // Obtener nombre: equipo para parejas, jugador para individual
                    const getNombre = () => {
                      if (item.equipo) {
                        return item.equipo.nombreEquipo || `Equipo ${item.equipo.equipoIndex + 1}`;
                      }
                      return item.jugador?.username || item.jugador?.nombre || 'N/A';
                    };
                    
                    // Verificar si es el usuario actual (para individual) o su equipo (para parejas)
                    const esUsuarioActual = () => {
                      if (item.equipo) {
                        return item.equipo.jugador1?._id === user._id || item.equipo.jugador2?._id === user._id;
                      }
                      return item.jugador?._id === user._id;
                    };
                    
                    return (
                      <tr 
                        key={item.jugador?._id || item.equipo?.equipoIndex || index}
                        className={`${esUsuarioActual() ? 'my-row' : ''} ${index < 3 ? `top-${index + 1}` : ''}`}
                      >
                        <td className={`pos pos-${index + 1}`}>
                          {index < 3 ? (
                            <span className="medal-emoji">
                              {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                            </span>
                          ) : (
                            index + 1
                          )}
                        </td>
                        <td className="jugador">
                          {getNombre()}
                          {esUsuarioActual() && ' 👤'}
                        </td>
                        <td className="points"><strong>{item.puntos || 0}</strong></td>
                        <td>{item.partidas || 0}</td>
                        <td className="wins">{item.victorias || 0}</td>
                        <td className="losses">{item.derrotas || 0}</td>
                        <td className="games-diff">{item.juegosGanados || 0}-{item.juegosPerdidos || 0}</td>
                        <td className={`diff ${diferencia > 0 ? 'positive' : diferencia < 0 ? 'negative' : 'neutral'}`}>
                          {diferencia > 0 ? '+' : ''}{diferencia}
                        </td>
                        <td className="ciento80s">{total180s}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Jornadas */}
      <div className="section">
        <h2>📅 Jornadas</h2>
        {loading ? (
          <div className="loading-small">Cargando...</div>
        ) : jornadas.length === 0 ? (
          <p className="empty-message">No hay jornadas disponibles</p>
        ) : (
          <>
            {/* Buscador de rivales */}
            <div className="search-rival-container">
              <input
                type="text"
                className="search-rival-input"
                placeholder="🔍 Buscar rival por nombre..."
                value={searchRival}
                onChange={(e) => {
                  setSearchRival(e.target.value);
                  setSuggestionSelected(false);
                }}
              />
              {searchRival && (
                <button 
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchRival('');
                    setSuggestionSelected(false);
                  }}
                >
                  ✕
                </button>
              )}
              
              {/* Sugerencias de rivales */}
              {searchRival.trim().length >= 2 && sugerenciasRivales.length > 0 && (
                <div className="suggestions-list">
                  {sugerenciasRivales.map((nombreRival, index) => (
                    <div 
                      key={index}
                      className="suggestion-item"
                      onClick={() => {
                        setSearchRival(nombreRival);
                        setSuggestionSelected(true);
                      }}
                    >
                      {nombreRival}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Resultados de búsqueda */}
            {searchRival && jornadasFiltradas.length === 0 ? (
              <p className="empty-message">No se encontraron partidas contra "{searchRival}"</p>
            ) : null}

            <div 
              className="jornadas-list" 
              style={{ marginTop: (searchRival.trim().length >= 2 && sugerenciasRivales.length > 0) ? '80px' : '0' }}
            >
              {jornadasFiltradas.map(jornada => {
              const isExpanded = expandedJornadas.has(jornada.jornada);
              
              // Verificar si el jugador ha completado SU partida en esta jornada
              const miPartida = jornada.partidas.find(p => 
                (p.jugador1._id === user._id || p.jugador2._id === user._id) && p.completada
              );
              const heJugado = !!miPartida;
              
              // Verificar si el jugador descansa en esta jornada
              const yoDescanso = jornada.descanso && jornada.descanso.jugador._id === user._id;

              return (
                <div 
                  key={jornada.jornada} 
                  className="jornada-container"
                  data-jornada={jornada.jornada}
                >
                  {/* Header de jornada - clickeable */}
                  <div 
                    className="jornada-header-expand" 
                    onClick={() => toggleJornada(jornada.jornada)}
                  >
                    <div className="jornada-title-row">
                      {heJugado && (
                        <span className="jornada-check">✓</span>
                      )}
                      {yoDescanso && (
                        <span className="jornada-rest">😴</span>
                      )}
                      <span className="jornada-num">Jornada {jornada.jornada}</span>
                      <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                    </div>
                  </div>

                  {/* Contenido expandible - todas las partidas */}
                  {isExpanded && (
                    <div className="jornada-content">
                      {/* Card de descanso si existe */}
                      {jornada.descanso && (
                        <div className="descanso-card-compact">
                          <span className="descanso-emoji">😴</span>
                          <span className="descanso-nombre">{jornada.descanso.jugador.username}</span>
                          <span className="descanso-label">(Descanso)</span>
                        </div>
                      )}
                      
                      {jornada.partidas.map(partida => {
                        const ganadorId = partida.resultado?.ganador?._id || partida.resultado?.ganador;
                        const player1Id = partida.jugador1._id || partida.jugador1;
                        const player2Id = partida.jugador2._id || partida.jugador2;
                        const esGanador1 = ganadorId === player1Id;
                        const esGanador2 = ganadorId === player2Id;
                        const isPartidaExpanded = expandedPartidas.has(partida._id);
                        const player1DisplayName = partida.nombreEquipo1 || partida.jugador1.username || 'Jugador 1';
                        const player2DisplayName = partida.nombreEquipo2 || partida.jugador2.username || 'Jugador 2';
                        const esJugador1Actual = player1Id === user._id;
                        const esJugador2Actual = player2Id === user._id;
                        const canExpandStats = partida.completada && partida.promedioAveragesPartida;
                        const advancedStats = partida.estadisticasAvanzadas;
                        const advancedStatsDisponibles = Boolean(advancedStats);
                        const statsItems = [
                          {
                            label: 'RESULTADO',
                            player1Value: partida.completada ? String(partida.resultado?.partidasGanadas?.jugador1 || 0) : '-',
                            player2Value: partida.completada ? String(partida.resultado?.partidasGanadas?.jugador2 || 0) : '-',
                            primary: true,
                          },
                          ...(canExpandStats && isPartidaExpanded ? [
                            {
                              label: 'AVRG',
                              player1Value: formatMatchStatValue(partida.promedioAveragesPartida.jugador1, { decimals: 1, fallback: '0.0' }),
                              player2Value: formatMatchStatValue(partida.promedioAveragesPartida.jugador2, { decimals: 1, fallback: '0.0' }),
                            },
                            {
                              label: '180',
                              player1Value: String(partida.count180s?.jugador1 || 0),
                              player2Value: String(partida.count180s?.jugador2 || 0),
                              highlight: true,
                            },
                            {
                              label: '100+',
                              player1Value: advancedStatsDisponibles ? String(advancedStats?.jugador1?.count100Plus || 0) : '-',
                              player2Value: advancedStatsDisponibles ? String(advancedStats?.jugador2?.count100Plus || 0) : '-',
                            },
                            {
                              label: '140+',
                              player1Value: advancedStatsDisponibles ? String(advancedStats?.jugador1?.count140Plus || 0) : '-',
                              player2Value: advancedStatsDisponibles ? String(advancedStats?.jugador2?.count140Plus || 0) : '-',
                            },
                            {
                              label: 'Checkout %',
                              player1Value: advancedStatsDisponibles
                                ? formatMatchStatValue(advancedStats?.jugador1?.checkoutPct, { decimals: 1, suffix: '%' })
                                : '-',
                              player2Value: advancedStatsDisponibles
                                ? formatMatchStatValue(advancedStats?.jugador2?.checkoutPct, { decimals: 1, suffix: '%' })
                                : '-',
                            },
                            {
                              label: 'High Finish',
                              player1Value: advancedStatsDisponibles && (advancedStats?.jugador1?.highFinish || 0) > 0
                                ? String(advancedStats?.jugador1?.highFinish)
                                : '-',
                              player2Value: advancedStatsDisponibles && (advancedStats?.jugador2?.highFinish || 0) > 0
                                ? String(advancedStats?.jugador2?.highFinish)
                                : '-',
                            },
                          ] : []),
                        ];
                        
                        return (
                          <div 
                            key={partida._id} 
                            className={`partida-card ${partida.completada ? 'completada' : 'pendiente'} ${canExpandStats ? 'expandable' : ''}`}
                            onClick={canExpandStats ? () => togglePartida(partida._id) : undefined}
                          >
                            {/* Badge del apuntador si existe */}
                            {partida.apuntador && (
                              <div className="apuntador-badge">
                                <span className="apuntador-badge-label">Apunta</span>
                                <span className="apuntador-badge-name">{partida.nombreEquipoApuntador || partida.apuntador.username || partida.apuntador.nombre}</span>
                              </div>
                            )}
                            
                            {/* Fecha y estado */}
                            <div className="partida-meta">
                              <span className="partida-fecha">
                                {formatearFecha(partida.fecha)}
                              </span>
                              <div className="partida-meta-actions">
                                <span className={`status-icon ${partida.completada ? 'completada' : 'pendiente'}`}>
                                  {partida.completada ? '✓' : '⏳'}
                                </span>
                                {canExpandStats && (
                                  <button
                                    type="button"
                                    className={`ver-stats-btn ${isPartidaExpanded ? 'expanded' : ''}`}
                                    aria-expanded={isPartidaExpanded}
                                    aria-label={isPartidaExpanded ? 'Ocultar estadísticas del partido' : 'Ver estadísticas del partido'}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      togglePartida(partida._id);
                                    }}
                                  >
                                    <span className="ver-stats-text ver-stats-text-desktop">
                                      {isPartidaExpanded ? 'Ocultar estadísticas' : 'Ver estadísticas'}
                                    </span>
                                    <span className="ver-stats-icon" aria-hidden="true">
                                      {isPartidaExpanded ? '▼' : '▶'}
                                    </span>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Enfrentamiento en formato horizontal compacto */}
                            <div className="enfrentamiento-horizontal">
                              <div className="partida-nombres-row">
                                <span className={`partida-nombre-slot left ${esJugador1Actual ? 'mine' : ''}`}>
                                  {player1DisplayName}
                                </span>
                                <span className="partida-nombres-vs">VS</span>
                                <span className={`partida-nombre-slot right ${esJugador2Actual ? 'mine' : ''}`}>
                                  {player2DisplayName}
                                </span>
                              </div>
                            </div>

                            <div className={`partida-estadisticas ${isPartidaExpanded ? 'expanded' : ''}`}>
                              {statsItems.map((statItem) => (
                                <div key={`${partida._id}-${statItem.label}`} className={`partida-stat-row ${statItem.primary ? 'primary' : ''}`}>
                                  <span className={`partida-stat-value left ${statItem.primary ? 'primary' : ''} ${statItem.highlight ? 'highlight' : ''} ${statItem.primary && esGanador1 ? 'winner' : ''}`}>{statItem.player1Value}</span>
                                  <span className={`partida-stat-label ${statItem.primary ? 'primary' : ''} ${statItem.highlight ? 'highlight' : ''}`}>{statItem.label}</span>
                                  <span className={`partida-stat-value right ${statItem.primary ? 'primary' : ''} ${statItem.highlight ? 'highlight' : ''} ${statItem.primary && esGanador2 ? 'winner' : ''}`}>{statItem.player2Value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Sección de Patrocinadores */}
      <SponsorsSection ref={sponsorsRef} />

        </>
      )}
    </div>
  );
};

export default Dashboard;
